<script lang="ts">
  import { onMount } from 'svelte';
  import {
    listAllOrgs, listOrgUsers, getOrgUsage, getUserUsage, superAdminDeleteUser,
    startSupportView,
    getDealtonFundQa, replayDealtonFundOnboarding, releaseDealtonFundOnboarding,
    type SuperAdminOrg, type SuperAdminUser, type UserUsageStats, type OrgUsageStats,
  } from '../lib/api';

  let { canSupportView = false, viewOnly = false }:
    { canSupportView?: boolean; viewOnly?: boolean } = $props();

  let orgs = $state<SuperAdminOrg[]>([]);
  let loading = $state(true);
  let error = $state('');
  let startDate = $state('');
  let endDate = $state('');
  let fundQa = $state<{ active: boolean; replay_owner: string | null } | null>(null);
  let replayBusy = $state(false);
  let replayError = $state('');

  async function loadFundQa() {
    try { fundQa = await getDealtonFundQa(); }
    catch (e: any) { replayError = e?.message || 'Could not load Fund QA status'; }
  }

  async function replayFundWizard() {
    replayBusy = true;
    replayError = '';
    try {
      await replayDealtonFundOnboarding();
      window.location.assign('/today');
    } catch (e: any) {
      replayError = e?.message || 'Could not start Fund onboarding';
      await loadFundQa();
    } finally { replayBusy = false; }
  }

  async function releaseFundWizard() {
    if (!fundQa?.replay_owner || replayBusy) return;
    if (!confirm(`End the current onboarding replay for ${fundQa.replay_owner}? Their unsaved wizard inputs will be lost. No saved Sales or Fund data will be deleted.`)) return;
    replayBusy = true;
    replayError = '';
    try {
      await releaseDealtonFundOnboarding();
      await loadFundQa();
    } catch (e: any) {
      replayError = e?.message || 'Could not end replay';
    } finally { replayBusy = false; }
  }

  // Per-org state: which is expanded, cached user list, loading state
  const expandedOrgs = $state<Record<string, boolean>>({});
  const orgUsers = $state<Record<string, SuperAdminUser[]>>({});
  const orgUsersLoading = $state<Record<string, boolean>>({});
  const orgUsage = $state<Record<string, OrgUsageStats>>({});
  const orgUsageLoading = $state<Record<string, boolean>>({});

  // Per-user state (keyed by `${company}|${email}`)
  const expandedUsers = $state<Record<string, boolean>>({});
  const userUsage = $state<Record<string, UserUsageStats>>({});
  const userUsageLoading = $state<Record<string, boolean>>({});

  const tierLabels: Record<string, string> = { '0': 'Trial', '2': 'Base', '3': 'Full', '': '—' };

  function tierLabel(t: string): string {
    // values are stored as JSON strings sometimes ("0", "2") — strip quotes
    const stripped = (t || '').replace(/^"+|"+$/g, '');
    return tierLabels[stripped] ?? stripped ?? '—';
  }

  function fmtDate(iso: string): string {
    if (!iso) return '—';
    const cleaned = iso.replace(/^"+|"+$/g, '');
    if (!cleaned) return '—';
    try {
      const d = new Date(cleaned.includes('T') ? cleaned : cleaned + 'T00:00:00Z');
      if (isNaN(d.getTime())) return cleaned;
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return cleaned; }
  }

  function fmtRelative(iso: string): string {
    if (!iso) return 'never';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      const sec = Math.floor((Date.now() - d.getTime()) / 1000);
      if (sec < 60) return `${sec}s ago`;
      if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
      if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
      const days = Math.floor(sec / 86400);
      if (days < 30) return `${days}d ago`;
      return d.toLocaleDateString();
    } catch { return iso; }
  }

  async function load() {
    loading = true;
    error = '';
    try {
      const r = await listAllOrgs(startDate || undefined, endDate || undefined);
      orgs = r.orgs;
    } catch (e: any) {
      error = e?.message || 'Failed to load orgs';
    }
    loading = false;
  }

  async function toggleOrg(company: string) {
    const willExpand = !expandedOrgs[company];
    expandedOrgs[company] = willExpand;
    if (willExpand) {
      // Lazy-load both users AND org-wide usage in parallel on first expand
      if (!orgUsers[company]) {
        orgUsersLoading[company] = true;
        listOrgUsers(company)
          .then((r) => { orgUsers[company] = r.users; })
          .catch((e: any) => { error = `Could not load users for ${company}: ${e?.message || ''}`; })
          .finally(() => { orgUsersLoading[company] = false; });
      }
      if (!orgUsage[company]) {
        orgUsageLoading[company] = true;
        getOrgUsage(company)
          .then((r) => { orgUsage[company] = r; })
          .catch((e: any) => { error = `Could not load org usage for ${company}: ${e?.message || ''}`; })
          .finally(() => { orgUsageLoading[company] = false; });
      }
    }
  }

  async function toggleUser(company: string, email: string) {
    const key = `${company}|${email}`;
    const willExpand = !expandedUsers[key];
    expandedUsers[key] = willExpand;
    if (willExpand && !userUsage[key]) {
      userUsageLoading[key] = true;
      try {
        userUsage[key] = await getUserUsage(company, email);
      } catch (e: any) {
        error = `Could not load usage for ${email}: ${e?.message || ''}`;
      }
      userUsageLoading[key] = false;
    }
  }

  // Deleting a user is destructive; deleting the sole user of an org
  // ALSO destroys every org-scoped row (~20 tables) + GCS blobs +
  // local cache — see admin._archive_org. The backend calls this
  // "archive" but nothing is recoverable; the only thing preserved is
  // a marker row that blocks the domain from starting a fresh trial.
  // Two confirms in the sole-user case; one otherwise.
  const deletingUsers = $state<Record<string, boolean>>({});
  const viewingUsers = $state<Record<string, boolean>>({});

  async function viewAsUser(company: string, email: string) {
    const key = `${company}|${email}`;
    if (viewingUsers[key]) return;
    // Open synchronously on click so browsers do not block the new tab while
    // the authenticated API request is in flight.
    const previewTab = window.open('about:blank', '_blank');
    if (!previewTab) { error = 'Allow pop-ups to open the support view.'; return; }
    previewTab.opener = null;
    viewingUsers[key] = true;
    try {
      const result = await startSupportView(company, email);
      previewTab.location.replace(`/today#support-view=${encodeURIComponent(result.token)}`);
    } catch (e: any) {
      previewTab.close();
      error = `Could not open ${email}: ${e?.message || 'Unknown error'}`;
    } finally {
      viewingUsers[key] = false;
    }
  }
  async function removeUser(company: string, email: string) {
    const key = `${company}|${email}`;
    if (deletingUsers[key]) return;
    const isLast = (orgUsers[company]?.length ?? 0) <= 1;
    const firstMsg = isLast
      ? `Delete ${email}?\n\nThis is the ONLY user in ${company}. Removing them will DELETE all of the org's data — every client, thread, draft, and file — from the database, GCS, and local cache. Nothing is recoverable. The only thing left will be a marker preventing ${company} from starting a fresh trial.`
      : `Delete ${email} from ${company}?\n\nTheir personal data (drafts, dismissals, personas, sessions) is wiped. The org's CRM data stays for the other users.`;
    if (!confirm(firstMsg)) return;
    if (isLast && !confirm(`Last chance to cancel.\n\nAll ${company} data will be permanently deleted. There is no undo, no export, no recovery.`)) return;
    deletingUsers[key] = true;
    try {
      const r = await superAdminDeleteUser(company, email);
      // Drop the user from the cached list so the UI reflects reality
      // without needing a full reload of the orgs page.
      if (orgUsers[company]) {
        orgUsers[company] = orgUsers[company].filter(u => u.email !== email);
      }
      if (r.org_archived) {
        // Org is gone — remove from the top-level list too and collapse.
        orgs = orgs.filter(o => o.company !== company);
        delete expandedOrgs[company];
        delete orgUsers[company];
        delete orgUsage[company];
      }
    } catch (e: any) {
      error = `Delete failed for ${email}: ${e?.message || 'Unknown error'}`;
    }
    deletingUsers[key] = false;
  }

  onMount(() => { load(); if (!viewOnly) loadFundQa(); });
</script>

<div class="page-title">Super Admin · All Orgs</div>
<div class="page-subtitle">Cross-tenant view (Dealton staff only)</div>

{#if fundQa?.active && !viewOnly}
  <div class="filter-card">
    <strong>Dealton Fund QA</strong>
    <p>The organization is temporarily using the Fund workflow. Replay the real Fund onboarding wizard for your account. The organization profile is shared, so only one staff member can replay at a time.</p>
    {#if fundQa.replay_owner}
      <p>Onboarding replay in progress: {fundQa.replay_owner}</p>
    {/if}
    <button class="filter-btn" onclick={replayFundWizard} disabled={replayBusy || !!fundQa.replay_owner}>
      {replayBusy ? 'Starting…' : 'Replay Fund onboarding'}
    </button>
    {#if fundQa.replay_owner}
      <button class="filter-btn-ghost" onclick={releaseFundWizard} disabled={replayBusy}>
        End current replay
      </button>
    {/if}
    {#if replayError}<p class="error-row">{replayError}</p>{/if}
  </div>
{/if}

<div class="filter-card">
  <div class="filter-row">
    <label class="filter-field">
      <span>Created from</span>
      <input type="date" bind:value={startDate} />
    </label>
    <label class="filter-field">
      <span>Created to</span>
      <input type="date" bind:value={endDate} />
    </label>
    <button class="filter-btn" onclick={load} disabled={loading}>
      {loading ? 'Loading…' : 'Refresh'}
    </button>
    {#if startDate || endDate}
      <button class="filter-btn-ghost" onclick={() => { startDate = ''; endDate = ''; load(); }}>
        Clear
      </button>
    {/if}
  </div>
  <div class="filter-meta">
    {orgs.length} org{orgs.length === 1 ? '' : 's'}
    {#if startDate || endDate}
      · filtered by trial-start
    {/if}
  </div>
</div>

{#if error}
  <p class="error-row">{error}</p>
{/if}

<table class="orgs-table">
  <thead>
    <tr>
      <th></th>
      <th>Company</th>
      <th>Created</th>
      <th>Tier</th>
      <th>Trial / Sub Ends</th>
      <th>Users</th>
    </tr>
  </thead>
  <tbody>
    {#each orgs as org}
      <tr class="org-row" onclick={() => toggleOrg(org.company)}>
        <td class="expand-cell">{expandedOrgs[org.company] ? '▾' : '▸'}</td>
        <td><strong>{org.company}</strong></td>
        <td>{fmtDate(org.trial_start)}</td>
        <td>
          {tierLabel(org.tier)}
          {#if org.trial_days_remaining !== null && org.trial_days_remaining !== undefined}
            <span class="trial-badge"
              class:trial-badge-warn={org.trial_days_remaining <= 3}
              class:trial-badge-expired={org.trial_days_remaining === 0}>
              {org.trial_days_remaining === 0 ? 'expired' : `${org.trial_days_remaining}d left`}
            </span>
          {/if}
        </td>
        <td>
          {#if org.trial_end}
            <span title="Trial ends">{fmtDate(org.trial_end)}</span>
          {:else if org.subscription_ends_at}
            <span title="Subscription ends">{fmtDate(org.subscription_ends_at)}</span>
          {:else}
            —
          {/if}
        </td>
        <td>{org.user_count}</td>
      </tr>
      {#if expandedOrgs[org.company]}
        <tr class="users-detail-row">
          <td></td>
          <td colspan="5">
            <!-- Org-wide temporal usage panel -->
            <div class="org-usage-panel">
              <div class="org-usage-title">Org-wide activity</div>
              {#if orgUsageLoading[org.company]}
                <p class="text-dim">Loading…</p>
              {:else if orgUsage[org.company]}
                {@const ou = orgUsage[org.company]}
                <table class="org-usage-table">
                  <thead>
                    <tr>
                      <th>Bucket</th>
                      <th>Last 7 days</th>
                      <th>Last 30 days</th>
                      <th>All-time</th>
                      <th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>💬 Chat</td>
                      <td>{ou.chat.count_7d}</td>
                      <td>{ou.chat.count_30d}</td>
                      <td>{ou.chat.count_all}</td>
                      <td>{fmtRelative(ou.chat.last_ts)}</td>
                    </tr>
                    <tr>
                      <td>🔍 Prospecting</td>
                      <td>{ou.prospecting.count_7d}</td>
                      <td>{ou.prospecting.count_30d}</td>
                      <td>{ou.prospecting.count_all}</td>
                      <td>{fmtRelative(ou.prospecting.last_ts)}</td>
                    </tr>
                    <tr>
                      <td>🎯 Strafe (jobs)</td>
                      <td>{ou.strafe.jobs_7d}</td>
                      <td>{ou.strafe.jobs_30d}</td>
                      <td>{ou.strafe.jobs_all} <span class="text-dim">/ {ou.strafe.total_recipients_all} recip</span></td>
                      <td>{fmtRelative(ou.strafe.last_ts)}</td>
                    </tr>
                    <tr>
                      <td>✉️ Client email</td>
                      <td>{ou.email_client.count_7d}</td>
                      <td>{ou.email_client.count_30d}</td>
                      <td>{ou.email_client.count_all}</td>
                      <td>{fmtRelative(ou.email_client.last_ts)}</td>
                    </tr>
                    <tr class="active-users-row">
                      <td>👥 Active users</td>
                      <td>{ou.active_users.users_7d} <span class="text-dim">/ {ou.active_users.total_users}</span></td>
                      <td>{ou.active_users.users_30d} <span class="text-dim">/ {ou.active_users.total_users}</span></td>
                      <td>{ou.active_users.users_all} <span class="text-dim">/ {ou.active_users.total_users}</span></td>
                      <td>{fmtRelative(ou.last_activity)}</td>
                    </tr>
                  </tbody>
                </table>
              {/if}
            </div>

            <div class="org-users-title">Users ({orgUsers[org.company]?.length ?? '…'})</div>
            {#if orgUsersLoading[org.company]}
              <p class="text-dim">Loading users…</p>
            {:else if orgUsers[org.company]}
              {#each orgUsers[org.company] as u}
                {@const key = `${org.company}|${u.email}`}
                <div class="user-block">
                  <div class="user-row" onclick={() => toggleUser(org.company, u.email)} role="button" tabindex="0">
                    <span class="user-expand">{expandedUsers[key] ? '▾' : '▸'}</span>
                    <span class="user-name">{u.name || '(no name)'}</span>
                    <span class="user-email">{u.email}</span>
                    {#if u.is_admin}
                      <span class="user-admin-badge">admin</span>
                    {/if}
                    {#if canSupportView}
                      <button type="button" class="filter-btn-ghost"
                        disabled={viewingUsers[key]}
                        onclick={(e) => { e.stopPropagation(); viewAsUser(org.company, u.email); }}
                        title={`Open a read-only view of ${u.email}`}>
                        {viewingUsers[key] ? 'Opening…' : 'View as'}
                      </button>
                    {/if}
                    {#if !viewOnly && !u.email.toLowerCase().endsWith('@dealton.ai')}
                      <button
                        type="button"
                        class="user-remove-btn"
                        disabled={deletingUsers[key]}
                        onclick={(e) => { e.stopPropagation(); removeUser(org.company, u.email); }}
                        title={(orgUsers[org.company]?.length ?? 0) <= 1
                          ? 'Delete user AND archive this org'
                          : 'Delete this user (org stays)'}
                      >{deletingUsers[key] ? 'Removing…' : 'Remove'}</button>
                    {/if}
                  </div>
                  {#if expandedUsers[key]}
                    <div class="usage-cards">
                      {#if userUsageLoading[key]}
                        <p class="text-dim">Loading usage…</p>
                      {:else if userUsage[key]}
                        {@const us = userUsage[key]}
                        <div class="stat-card">
                          <div class="stat-label">💬 Chat</div>
                          <div class="stat-numbers">{us.chat.count_30d} <span class="stat-divider">/</span> {us.chat.count_all}</div>
                          <div class="stat-meta">30d / all-time</div>
                          <div class="stat-last">{fmtRelative(us.chat.last_ts)}</div>
                        </div>
                        <div class="stat-card">
                          <div class="stat-label">🔍 Prospecting</div>
                          <div class="stat-numbers">{us.prospecting.count_30d} <span class="stat-divider">/</span> {us.prospecting.count_all}</div>
                          <div class="stat-meta">30d / all-time</div>
                          <div class="stat-last">{fmtRelative(us.prospecting.last_ts)}</div>
                        </div>
                        <div class="stat-card">
                          <div class="stat-label">🎯 Strafe</div>
                          <div class="stat-numbers">{us.strafe.jobs_30d} <span class="stat-divider">/</span> {us.strafe.jobs_all}</div>
                          <div class="stat-meta">jobs 30d / all-time · {us.strafe.total_recipients_all} recipients total</div>
                          <div class="stat-last">{fmtRelative(us.strafe.last_ts)}</div>
                        </div>
                        <div class="stat-card">
                          <div class="stat-label">✉️ Client email</div>
                          <div class="stat-numbers">{us.email_client.count_30d} <span class="stat-divider">/</span> {us.email_client.count_all}</div>
                          <div class="stat-meta">30d / all-time</div>
                          <div class="stat-last">{fmtRelative(us.email_client.last_ts)}</div>
                        </div>
                        <div class="stat-card stat-card-wide">
                          <div class="stat-label">⏱ Last activity <span class="stat-tooltip" title="Based on most recent API call, scheduled email, strafe job, or George call. No login tracking exists.">(?)</span></div>
                          <div class="stat-numbers stat-numbers-sm">{fmtRelative(us.last_activity)}</div>
                          <div class="stat-meta">{us.last_activity || 'never'}</div>
                        </div>
                      {/if}
                    </div>
                  {/if}
                </div>
              {/each}
              {#if orgUsers[org.company].length === 0}
                <p class="text-dim">No users in this org.</p>
              {/if}
            {/if}
          </td>
        </tr>
      {/if}
    {/each}
  </tbody>
</table>

{#if !loading && orgs.length === 0}
  <p class="text-dim" style="margin-top:24px;">No orgs match the current filter.</p>
{/if}

<style>
  .filter-card {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;
  }
  .filter-row {
    display: flex;
    gap: 12px;
    align-items: end;
    flex-wrap: wrap;
  }
  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .filter-field input {
    padding: 6px 10px;
    border: 1px solid var(--border);
    border-radius: 6px;
    font-size: 13px;
    background: var(--bg-surface);
    color: var(--text);
  }
  .filter-btn, .filter-btn-ghost {
    padding: 7px 16px;
    border-radius: 6px;
    border: 1px solid var(--border);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .filter-btn { background: var(--accent, #3b82f6); color: #fff; border-color: transparent; }
  .filter-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .filter-btn-ghost { background: transparent; color: var(--text-muted); }
  .filter-meta {
    margin-top: 8px;
    font-size: 12px;
    color: var(--text-muted);
  }
  .orgs-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 16px;
    font-size: 13px;
  }
  .orgs-table th, .orgs-table td {
    height: var(--row-h);
    text-align: left;
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
  }
  .orgs-table th { font-weight: 600; color: var(--text-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.04em; }
  .org-row { cursor: pointer; }
  .org-row:hover { background: var(--bg-hover, rgba(0,0,0,0.03)); }
  .expand-cell { width: 24px; color: var(--text-muted); }
  .users-detail-row td { background: var(--bg-hover); padding: 12px 16px; }
  .user-block { margin: 4px 0 8px; }
  .user-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 4px;
    cursor: pointer;
    border-radius: 4px;
  }
  .user-row:hover { background: var(--bg-hover, rgba(0,0,0,0.04)); }
  .user-expand { color: var(--text-muted); width: 14px; }
  .user-name { font-weight: 600; min-width: 140px; }
  .user-email { color: var(--text-muted); font-size: 12px; }
  .user-admin-badge {
    background: #dbeafe;
    color: #1e40af;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  /* Push destructive action to the far right so it doesn't sit alongside
     the informational fields (name / email / admin badge). Muted until
     hover to keep the row scannable — you don't want the red button
     yelling on every row in a long org list. */
  .user-remove-btn {
    margin-left: auto;
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    line-height: 1.4;
  }
  .user-remove-btn:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #ef4444;
    color: #b91c1c;
  }
  .user-remove-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .usage-cards {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 24px 16px;
  }
  .stat-card {
    flex: 1 1 140px;
    min-width: 140px;
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 12px 14px;
  }
  .stat-card-wide { flex: 2 1 280px; }
  .stat-label { font-size: 11px; color: var(--text-muted); font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.04em; }
  .stat-numbers { font-size: 22px; font-weight: 700; color: var(--text); }
  .stat-numbers-sm { font-size: 16px; }
  .stat-divider { color: var(--text-muted); font-weight: 400; margin: 0 2px; }
  .stat-meta { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .stat-last { font-size: 12px; color: var(--text-muted); margin-top: 6px; font-style: italic; }
  .stat-tooltip { color: var(--text-muted); cursor: help; font-size: 10px; }
  .error-row { color: #ef4444; padding: 10px 12px; background: rgba(239,68,68,0.05); border-radius: 6px; font-size: 13px; }
  .text-dim { color: var(--text-muted); font-size: 12px; }

  /* Trial badge in the orgs table */
  .trial-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: #dbeafe;
    color: #1e40af;
  }
  .trial-badge-warn {
    background: #fef3c7;
    color: #92400e;
  }
  .trial-badge-expired {
    background: #fee2e2;
    color: #991b1b;
  }

  /* Org-wide usage panel inside an expanded org */
  .org-usage-panel {
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 14px 16px;
    margin: 4px 0 16px;
  }
  .org-usage-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
  }
  .org-usage-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .org-usage-table th, .org-usage-table td {
    text-align: left;
    padding: 6px 10px;
    border-bottom: 1px solid var(--border);
  }
  .org-usage-table th {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    font-weight: 600;
  }
  .org-usage-table tbody tr:last-child td { border-bottom: none; }
  .active-users-row td {
    border-top: 2px solid var(--border);
    padding-top: 10px;
    font-weight: 600;
  }

  .org-users-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
  }
</style>
